import { LocationType as AvailabilityLocationType } from '@wix/ambassador-bookings-availability-v1-slot-availability/types';
import { LocationType as TimeslotLocationType } from '@wix/ambassador-bookings-availability-v2-time-slot/types';
import { ReservedLocationIds } from '@wix/bookings-uou-types';
import { BOOKINGS_APP_DEF_ID, BOOKINGS_CHECKOUT_SECTION_ID, BOOKINGS_FORM_SECTION_ID, BOOKINGS_SCHEDULER_SECTION_ID, BookingsQueryParams } from './consts';
import { getUrlQueryParams, isBookCheckoutInstalled, isBookingFormInstalled, setZeroMSToIsoDate } from './utils';
const getSectionRelativeUrl = async function (wixCodeApi, sectionId, isRouterPrefixNavigationEnable) {
  if (isRouterPrefixNavigationEnable === void 0) {
    isRouterPrefixNavigationEnable = false;
  }
  const {
    url: fullBookingsUrl,
    prefix
  } = await wixCodeApi.site.getSectionUrl({
    sectionId,
    appDefinitionId: BOOKINGS_APP_DEF_ID
  });
  if (isRouterPrefixNavigationEnable && prefix) {
    return `/${prefix}`;
  }
  const baseUrl = wixCodeApi.location.baseUrl;
  if (baseUrl) {
    const urlParts = fullBookingsUrl.split(baseUrl);
    return urlParts.length > 1 ? urlParts[1] : fullBookingsUrl;
  }
  return fullBookingsUrl;
};
const removeFromSessionStorage = (platformAPIs, key) => {
  return platformAPIs.storage.session.removeItem(key);
};
const setToSessionStorage = (platformAPIs, key, value) => {
  platformAPIs.storage.session.setItem(key, value);
};
const convertDynamicPriceToMultiDynamicPrice = (dynamicPriceInfo, slotAvailability) => {
  var _slotAvailability$slo, _slotAvailability$slo2;
  if (Array.isArray(dynamicPriceInfo)) {
    return dynamicPriceInfo;
  }
  return [{
    dynamicPriceInfo: dynamicPriceInfo,
    serviceId: slotAvailability == null || (_slotAvailability$slo = slotAvailability.slot) == null ? void 0 : _slotAvailability$slo.serviceId,
    startDate: slotAvailability == null || (_slotAvailability$slo2 = slotAvailability.slot) == null ? void 0 : _slotAvailability$slo2.startDate
  }];
};
const convertAddonsInfoToMultiAddonsInfo = (addonsInfo, slotAvailability) => {
  var _slotAvailability$slo3, _slotAvailability$slo4;
  if (Array.isArray(addonsInfo)) {
    return addonsInfo;
  }
  return [{
    serviceId: slotAvailability == null || (_slotAvailability$slo3 = slotAvailability.slot) == null ? void 0 : _slotAvailability$slo3.serviceId,
    startDate: slotAvailability == null || (_slotAvailability$slo4 = slotAvailability.slot) == null ? void 0 : _slotAvailability$slo4.startDate,
    addonsInfo: [addonsInfo]
  }];
};
const removeUndefinedKeys = obj => {
  return Object.fromEntries(Object.entries(obj).filter(_ref => {
    let [_, v] = _ref;
    return v !== undefined;
  }));
};
const queryBuilder = function (wixCodeApi, queryParams, copyCurrentQueryParams) {
  if (copyCurrentQueryParams === void 0) {
    copyCurrentQueryParams = true;
  }
  const currentParams = copyCurrentQueryParams ? getUrlQueryParams(wixCodeApi) : {};
  const params = [];
  for (const [key, value] of Object.entries({
    ...currentParams,
    ...removeUndefinedKeys(queryParams)
  })) {
    if (value) {
      params.push([`${key}=${value}`]);
    }
  }
  return params.length ? `?${params.join('&')}` : '';
};
const navigateToCourseContactInfoPage = (wixCodeApi, serviceSlug, referral, timezone) => {
  const suffix = `/${serviceSlug}/book`;
  const queryParams = {
    referral,
    timezone
  };
  return navigateToBookingsWithSuffix(wixCodeApi, `${suffix}${queryBuilder(wixCodeApi, queryParams)}`);
};
const getBookingsRelativeUrl = async wixCodeApi => {
  const isBookingCheckoutInstalled = await isBookCheckoutInstalled(wixCodeApi);
  const fullBookingsUrl = (await wixCodeApi.site.getSectionUrl({
    sectionId: isBookingCheckoutInstalled ? BOOKINGS_CHECKOUT_SECTION_ID : BOOKINGS_SCHEDULER_SECTION_ID,
    appDefinitionId: BOOKINGS_APP_DEF_ID
  })).url;
  const baseUrl = wixCodeApi.location.baseUrl;
  if (baseUrl) {
    const urlParts = fullBookingsUrl.split(baseUrl);
    return urlParts.length > 1 ? urlParts[1] : fullBookingsUrl;
  }
  return fullBookingsUrl;
};
const navigateToBookingsWithSuffix = async function (wixCodeApi, suffix) {
  if (suffix === void 0) {
    suffix = '';
  }
  const bookingsRelativeUrl = await getBookingsRelativeUrl(wixCodeApi);
  wixCodeApi.location.to(bookingsRelativeUrl + suffix);
};
const navigateToBookingsContactInfoPage = (wixCodeApi, sessionDetails, referral, timezone) => {
  const suffix = `/${sessionDetails.slug}/book/${sessionDetails.scheduleId}/${sessionDetails.start}/${sessionDetails.end}/${sessionDetails.staffMemberId}`;
  const queryParams = {
    ...(sessionDetails ? {
      referral
    } : {}),
    timezone
  };
  const locationSuffix = sessionDetails.locationId ? `/${sessionDetails.locationId}` : `/${ReservedLocationIds.UNSPECIFIED_LOCATION}`;
  const sessionIdSuffix = sessionDetails.id ? `/${sessionDetails.id}` : '';
  return navigateToBookingsWithSuffix(wixCodeApi, `${suffix}${locationSuffix}${sessionIdSuffix}${queryBuilder(wixCodeApi, queryParams)}`);
};
const convertTimeSlotLocationToSlotAvailabilityLocation = location => {
  let locationType;
  switch (location.locationType) {
    case TimeslotLocationType.BUSINESS:
      locationType = AvailabilityLocationType.OWNER_BUSINESS;
      break;
    case TimeslotLocationType.CUSTOM:
      locationType = AvailabilityLocationType.OWNER_CUSTOM;
      break;
    case TimeslotLocationType.CUSTOMER:
      locationType = AvailabilityLocationType.CUSTOM;
      break;
    case TimeslotLocationType.UNKNOWN_LOCATION_TYPE:
      locationType = AvailabilityLocationType.UNDEFINED;
      break;
    default:
      locationType = undefined;
  }
  return {
    id: location.id ?? undefined,
    name: location.name ?? undefined,
    formattedAddress: location.formattedAddress ?? undefined,
    locationType
  };
};
export const getStaffMemberFromTimeSlotResources = (availableResources, timeSlotPreferences) => {
  var _availableStaffMember, _availableStaffMember2, _availableStaffMember3;
  if (!availableResources) {
    return undefined;
  }
  const STAFF_RESOURCE_TYPE_ID = '1cd44cf8-756f-41c3-bd90-3e2ffcaf1155';
  const selectedStaffPreferences = timeSlotPreferences == null ? void 0 : timeSlotPreferences.selectedResources.find(resource => resource.resourceTypeId === STAFF_RESOURCE_TYPE_ID);
  const availableStaffMember = availableResources == null ? void 0 : availableResources.find(availableResource => (availableResource == null ? void 0 : availableResource.resourceTypeId) === STAFF_RESOURCE_TYPE_ID);
  const resource = selectedStaffPreferences ? (availableStaffMember == null || (_availableStaffMember = availableStaffMember.resources) == null ? void 0 : _availableStaffMember.find(resource => resource.id === selectedStaffPreferences.selectedResourceId)) || (availableStaffMember == null || (_availableStaffMember2 = availableStaffMember.resources) == null ? void 0 : _availableStaffMember2[0]) : availableStaffMember == null || (_availableStaffMember3 = availableStaffMember.resources) == null ? void 0 : _availableStaffMember3[0];
  return resource ? {
    id: resource == null ? void 0 : resource.id,
    name: (resource == null ? void 0 : resource.name) ?? undefined
  } : undefined;
};
export const timeSlotToSlotAvailability = (timeSlot, timeSlotPreferences) => {
  var _timeSlot$eventInfo, _timeSlot$eventInfo2;
  return {
    bookable: timeSlot.bookable ?? false,
    bookingPolicyViolations: timeSlot.bookingPolicyViolations ? {
      bookOnlineDisabled: timeSlot.bookingPolicyViolations.bookOnlineDisabled ?? false,
      tooEarlyToBook: timeSlot.bookingPolicyViolations.tooEarlyToBook ?? false,
      tooLateToBook: timeSlot.bookingPolicyViolations.tooLateToBook ?? false
    } : undefined,
    slot: {
      scheduleId: timeSlot.scheduleId ?? undefined,
      eventId: ((_timeSlot$eventInfo = timeSlot.eventInfo) == null ? void 0 : _timeSlot$eventInfo.eventId) ?? undefined,
      serviceId: timeSlot.serviceId ?? undefined,
      startDate: setZeroMSToIsoDate(timeSlot.localStartDate ?? undefined),
      endDate: setZeroMSToIsoDate(timeSlot.localEndDate ?? undefined),
      resource: getStaffMemberFromTimeSlotResources(timeSlot.availableResources, timeSlotPreferences),
      location: timeSlot.location ? convertTimeSlotLocationToSlotAvailabilityLocation(timeSlot.location) : undefined
    },
    nestedSlots: [...(timeSlot.nestedTimeSlots || []).map(nestedTimeSlot => ({
      serviceId: nestedTimeSlot.serviceId ?? undefined,
      start: nestedTimeSlot.localStartDate ?? undefined,
      end: nestedTimeSlot.localEndDate ?? undefined,
      scheduleId: nestedTimeSlot.scheduleId ?? undefined,
      resource: getStaffMemberFromTimeSlotResources(nestedTimeSlot.availableResources)
    }))],
    totalSpots: timeSlot.totalCapacity ?? undefined,
    openSpots: timeSlot.remainingCapacity ?? undefined,
    waitingList: (_timeSlot$eventInfo2 = timeSlot.eventInfo) != null && _timeSlot$eventInfo2.waitingList ? {
      totalSpots: timeSlot.eventInfo.waitingList.totalCapacity ?? undefined,
      openSpots: timeSlot.eventInfo.waitingList.remainingCapacity ?? undefined
    } : undefined
  };
};
export const navigateToBookingsFormPage = async _ref2 => {
  let {
    platformAPIs,
    wixCodeApi,
    slotAvailability,
    timeSlot,
    serviceId,
    serviceSlug,
    dynamicPriceInfo,
    addonsInfo,
    ecom,
    queryParams = {},
    timeSlotPreferences,
    resourceSelectionStrategy
  } = _ref2;
  const {
    timezone,
    referral,
    continueShoppingUrl
  } = queryParams;
  if (await isBookingFormInstalled(wixCodeApi)) {
    const formUrl = await getSectionRelativeUrl(wixCodeApi, BOOKINGS_FORM_SECTION_ID);
    removeFromSessionStorage(platformAPIs, BookingsQueryParams.AVAILABILITY_SLOT);
    removeFromSessionStorage(platformAPIs, BookingsQueryParams.SERVICE_ID);
    removeFromSessionStorage(platformAPIs, BookingsQueryParams.TIMEZONE);
    removeFromSessionStorage(platformAPIs, BookingsQueryParams.DYNAMIC_PRICING);
    removeFromSessionStorage(platformAPIs, BookingsQueryParams.ECOM);
    removeFromSessionStorage(platformAPIs, BookingsQueryParams.ADDONS_INFO);
    removeFromSessionStorage(platformAPIs, BookingsQueryParams.RESOURCE_SELECTION_STRATEGY);
    slotAvailability && setToSessionStorage(platformAPIs, BookingsQueryParams.AVAILABILITY_SLOT, JSON.stringify(slotAvailability));
    timeSlot && setToSessionStorage(platformAPIs, BookingsQueryParams.AVAILABILITY_SLOT, JSON.stringify(timeSlotToSlotAvailability(timeSlot, timeSlotPreferences)));
    serviceId && setToSessionStorage(platformAPIs, BookingsQueryParams.SERVICE_ID, JSON.stringify(serviceId));
    timezone && setToSessionStorage(platformAPIs, BookingsQueryParams.TIMEZONE, JSON.stringify(timezone));
    dynamicPriceInfo && setToSessionStorage(platformAPIs, BookingsQueryParams.DYNAMIC_PRICING, JSON.stringify(convertDynamicPriceToMultiDynamicPrice(dynamicPriceInfo, slotAvailability)));
    ecom && setToSessionStorage(platformAPIs, BookingsQueryParams.ECOM, JSON.stringify(ecom));
    addonsInfo && setToSessionStorage(platformAPIs, BookingsQueryParams.ADDONS_INFO, JSON.stringify(convertAddonsInfoToMultiAddonsInfo(addonsInfo, slotAvailability)));
    resourceSelectionStrategy && setToSessionStorage(platformAPIs, BookingsQueryParams.RESOURCE_SELECTION_STRATEGY, JSON.stringify(resourceSelectionStrategy));
    return wixCodeApi.location.to(`${formUrl}${queryBuilder(wixCodeApi, {
      referral,
      continueShoppingUrl
    })}`);
  } else {
    if (slotAvailability) {
      var _resource, _location;
      const sessionDetails = {
        id: slotAvailability.slot.sessionId ?? undefined,
        slug: serviceSlug,
        scheduleId: slotAvailability.slot.scheduleId,
        start: new Date(slotAvailability.slot.startDate).valueOf(),
        end: new Date(slotAvailability.slot.endDate).valueOf(),
        staffMemberId: (_resource = slotAvailability.slot.resource) == null ? void 0 : _resource.id,
        locationId: (_location = slotAvailability.slot.location) == null ? void 0 : _location.id
      };
      return navigateToBookingsContactInfoPage(wixCodeApi, sessionDetails, referral, timezone);
    } else {
      return navigateToCourseContactInfoPage(wixCodeApi, serviceSlug, referral, timezone);
    }
  }
};
//# sourceMappingURL=navigate-to-bookings-form-page.js.map